import { useLang } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const T = {
  fr: {
    back: "Retour à l'accueil",
    title: "Politique de confidentialité",
    intro:
      "Cette page décrit comment Sereno collecte, utilise et protège vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD).",
    sections: [
      {
        title: "Données que nous collectons",
        body:
          "Lors de votre inscription : votre prénom, votre adresse email, votre langue et votre pays. Lors de votre utilisation : vos revenus, dépenses et épargne mensuels que vous saisissez volontairement, votre profil de risque, vos recommandations enregistrées, vos badges et vos check-ins mensuels. Nous ne collectons JAMAIS votre numéro de carte bancaire, votre numéro de sécurité sociale, ni aucun code d'accès à votre banque.",
      },
      {
        title: "Comment nous utilisons vos données",
        body:
          "Vos données servent uniquement à vous fournir le service Sereno : calculer vos projections, sauvegarder vos préférences, vous envoyer les emails que vous demandez (astuce de la semaine, réinitialisation de mot de passe, partages). Nous ne vendons AUCUNE donnée à des tiers.",
      },
      {
        title: "Sous-traitants",
        body:
          "Nous utilisons Resend (envoi d'emails), Emergent Labs (hébergement) et Anthropic (assistant IA anti-arnaque). Ces prestataires sont conformes RGPD et n'utilisent vos données que pour le service demandé.",
      },
      {
        title: "Durée de conservation",
        body:
          "Vos données sont conservées tant que votre compte est actif. Vous pouvez à tout moment demander la suppression de votre compte en écrivant à bonjour@sereno.money.",
      },
      {
        title: "Vos droits (RGPD)",
        body:
          "Vous avez le droit d'accéder à vos données, de les corriger, de les supprimer, de les recevoir dans un format portable et de vous opposer à leur traitement. Pour exercer ces droits, écrivez à bonjour@sereno.money.",
      },
      {
        title: "Cookies",
        body:
          "Sereno utilise uniquement un cookie technique nécessaire à votre session (stockage local du jeton de connexion). Aucun cookie de tracking publicitaire. Aucun cookie tiers.",
      },
      {
        title: "Sécurité",
        body:
          "Vos mots de passe sont chiffrés (bcrypt). Vos données transitent en HTTPS. Nous avons mis en place plusieurs protections : limite de tentatives de connexion, verrouillage après 5 échecs, honeypot anti-bots, headers de sécurité HTTP (HSTS, X-Frame-Options).",
      },
    ],
  },
  en: {
    back: "Back to home",
    title: "Privacy policy",
    intro:
      "This page describes how Sereno collects, uses and protects your personal data in accordance with the General Data Protection Regulation (GDPR).",
    sections: [
      {
        title: "Data we collect",
        body:
          "At registration: first name, email address, language, country. During use: monthly income, expenses and savings you enter voluntarily, risk profile, saved recommendations, badges and monthly check-ins. We NEVER collect your credit card number, social security number, or bank access codes.",
      },
      {
        title: "How we use your data",
        body:
          "Your data is used only to provide the Sereno service: compute projections, save preferences, send you the emails you request (weekly tip, password reset, shares). We NEVER sell any data to third parties.",
      },
      {
        title: "Third-party processors",
        body:
          "We use Resend (email), Emergent Labs (hosting) and Anthropic (AI anti-scam assistant). These providers are GDPR compliant and only use your data for the requested service.",
      },
      {
        title: "Retention period",
        body:
          "Your data is kept as long as your account is active. You can request deletion of your account at any time by writing to bonjour@sereno.money.",
      },
      {
        title: "Your rights (GDPR)",
        body:
          "You have the right to access, correct, delete, port and object to processing of your data. To exercise these rights, write to bonjour@sereno.money.",
      },
      {
        title: "Cookies",
        body:
          "Sereno uses only a technical cookie necessary for your session (local storage of the login token). No advertising tracking cookies. No third-party cookies.",
      },
      {
        title: "Security",
        body:
          "Your passwords are encrypted (bcrypt). Your data travels over HTTPS. We have implemented multiple protections: login attempt limits, lockout after 5 failures, anti-bot honeypot, HTTP security headers (HSTS, X-Frame-Options).",
      },
    ],
  },
};

export default function Privacy() {
  const { lang } = useLang();
  const tr = T[lang] || T.fr;
  return (
    <div className="mx-auto max-w-3xl px-6 py-12" data-testid="privacy-page">
      <Link
        to="/"
        data-testid="back-home-link"
        className="mb-6 inline-flex items-center gap-2 text-base text-[#101A42] hover:text-[#A762B1]"
      >
        <ArrowLeft className="h-4 w-4" />
        {tr.back}
      </Link>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#A762B1]/15 text-[#A762B1]">
          <ShieldCheck className="h-7 w-7" />
        </div>
        <h1 className="font-serif text-4xl font-semibold text-[#101A42] sm:text-5xl">
          {tr.title}
        </h1>
      </div>
      <p className="text-lg leading-relaxed text-[#4A4A4A]">{tr.intro}</p>
      {tr.sections.map((s, i) => (
        <section
          key={i}
          className="mt-6 rounded-2xl border-2 border-[#E8DFC7] bg-white p-6"
          data-testid={`privacy-section-${i}`}
        >
          <h2 className="font-serif text-xl font-semibold text-[#101A42]">
            {s.title}
          </h2>
          <p className="mt-2 text-base leading-relaxed text-[#101A42]">
            {s.body}
          </p>
        </section>
      ))}
    </div>
  );
}
